import { useEffect, useState, type ReactNode } from 'react';
import {
  WORKSPACE_TIMEZONES,
  billingDetailsSchema,
  currencySettingsSchema,
  rolesResponseSchema,
  setupWorkspaceResponseSchema,
  workspaceSettingsSchema,
} from '@forecastify/shared';
import { Button, SetupActions, SetupFrame, SetupStep } from '@forecastify/ui';
import logo from '@forecastify/ui/assets/forecastify-logo.png';
import logoWhite from '@forecastify/ui/assets/forecastify-logo-white.png';
import { useI18n } from '../../i18n';
import { useSession } from '../../session';
import { useWelcomeTransition } from '../../welcome-transition';
import { useAvatar, useAvatars, forgetAvatars } from '../use-avatar';
import { DoneStep } from './done-step';
import {
  EMPTY_INVITES,
  InviteStep,
  LEVELS,
  invitable,
  inviteBlocked,
  type InviteValues,
  type Level,
} from './invite-step';
import { SetupPreview } from './setup-preview';
import { pendingAvatar } from './setup-store';
import { useObjectUrl } from './use-object-url';
import { EMPTY_PROFILE, ProfileStep, profileBlocked, type ProfileValues } from './profile-step';
import { personInitials, personName } from './setup-rules';
import { useAddressCheck } from './use-address-check';
import {
  WorkspaceStep,
  workspaceBlocked,
  workspaceValuesFrom,
  type WorkspaceValues,
} from './workspace-step';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The first-run setup flow, steps 2 to 4 — rendered by `Gate` in place.     │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `Gate` renders this for a signed-in person with no workspace (step 2) and for one whose setup is
 * unfinished (step 3, in the workspace step 2 made) — whatever address they asked for, exactly as
 * it rendered the welcome screen this replaces. Step 1 happens where the account is made: the
 * invitation page (`invite-screen.tsx`).
 *
 * ⚠️⚠️ **EVERY STEP IS SAVED WHEN IT IS SUBMITTED, SO THERE IS NO "BACK".** The mockup draws a
 * *Vissza* button because nothing in it persists. Here step 2 creates a workspace and step 3 sends
 * mail; going back to either would offer to undo what cannot be undone by going back. A reload
 * resumes at the first unfinished step instead, which is the property that matters.
 *
 * ⚠️ **Mounted once across the reload between steps 2 and 3.** `Gate` renders it from the same
 * position for both states, so the session reload after step 2 re-renders this rather than
 * remounting it — which is what keeps the logo's preview on screen through step 4.
 */
export function SetupScreen(): ReactNode {
  const { t, locale } = useI18n();
  const session = useSession();
  const welcome = useWelcomeTransition();
  const workspace = session.workspaces.find((entry) => entry.id === session.workspaceId) ?? null;

  const [profile, setProfile] = useState<ProfileValues>(EMPTY_PROFILE);
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const [values, setValues] = useState<WorkspaceValues>(() =>
    workspaceValuesFrom(session.setup.organisationName ?? ''),
  );
  const [logoError, setLogoError] = useState<string | null>(null);
  const [invites, setInvites] = useState<InviteValues>(EMPTY_INVITES);
  const [failures, setFailures] = useState<readonly string[]>([]);
  const [sent, setSent] = useState(0);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [roles, setRoles] = useState<Readonly<Partial<Record<Level, string>>>>({});
  const [seats, setSeats] = useState<{ used: number; limit: number | null } | null>(null);
  /**
   * ⚠️⚠️ **BACK FROM STEP 3 REOPENS STEP 2 ON THE WORKSPACE IT MADE — 14 Oct 2026.** Every step is
   * saved when it is submitted, so going back cannot undo step 2; it edits what step 2 created.
   * `version` is the settings row's, for the PATCH — set while editing, `null` otherwise.
   * `currency` is the default it had when reopened (U-13), so only a changed one is sent.
   */
  const [editing, setEditing] = useState<{ version: number; currency: string | null } | null>(null);
  /**
   * ⚠️⚠️ **STEP 3 SENDS NOTHING — 14 Oct 2026, the product owner:** *"the invitations shouldn't be
   * sent out until step 4 is finished, that way the user can modify already entered values without
   * having to worry about already sent invites."* Step 3 collects the addresses; *Belépés* on step 4
   * sends them, then finishes. `skipped` is *Kihagyom*: step 4 sends nothing, and Back restores the
   * list as it was rather than throwing it away.
   */
  const [skipped, setSkipped] = useState(false);

  /* ⚠️ The organisation's name may arrive after the first render (the session is still reading
     `/auth/me`); it fills the field only while nobody has typed in it. */
  const organisationName = session.setup.organisationName;
  useEffect(() => {
    if (organisationName === null) return;
    setValues((current) => (current.name === '' ? workspaceValuesFrom(organisationName) : current));
  }, [organisationName]);

  /* ⚠️ `0203` · step 1 here only for flow A — an account the emailed code signed in, with no name
     and no password of its own. Everybody else did step 1 on the invitation page. */
  const step: 1 | 2 | 3 | 4 =
    workspace === null ? (session.setup.profileDone ? 2 : 1) : editing !== null ? 2 : done ? 4 : 3;
  const check = useAddressCheck(
    step === 2 ? values.slug : '',
    values.name,
    editing === null ? null : (workspace?.slug ?? null),
  );

  /* The pictures: what was picked, until the server has its own copy to serve. */
  const heldLogo = useObjectUrl(values.logo);
  const pickedAvatar = useObjectUrl(profile.avatar);
  const heldAvatar = useObjectUrl(pendingAvatar.peek());
  const savedAvatar = useAvatar(session.avatarUrl);
  const icons = useAvatars([
    workspace?.hasIcon === true ? `/files/workspace-icon/${workspace.id}` : null,
  ]);
  const savedLogo =
    workspace?.hasIcon === true ? icons.get(`/files/workspace-icon/${workspace.id}`) : undefined;
  const logoSrc = heldLogo ?? savedLogo ?? null;
  const avatarSrc = savedAvatar ?? heldAvatar ?? pickedAvatar ?? null;

  /* Step 1 shows what is being typed; afterwards the account's own name. */
  const name =
    step === 1 ? personName(profile.family, profile.given, locale) : (session.displayName ?? '');
  const initials =
    step === 1
      ? personInitials(profile.family, profile.given, locale)
      : name
          .split(/\s+/)
          .filter((word) => word !== '')
          .slice(0, 2)
          .map((word) => word[0] ?? '')
          .join('')
          .toUpperCase();
  const email = session.email ?? '';
  const host = window.location.host;
  /* While step 2 is open — first time or reopened — the preview shows what is being typed. */
  const workspaceName = step === 2 ? values.name : (workspace?.name ?? values.name);

  /* Step 3 reads the roles to invite with and the seats left. Both are the workspace's, so they
     wait for step 2 to have made one. */
  useEffect(() => {
    if (workspace === null) return;
    /* An object, so the cleanup's write is visible to the awaits — a `let` is narrowed to `true`. */
    const alive = { current: true };
    void (async () => {
      const rolesResponse = await session.request('/permissions/roles').catch(() => null);
      const parsedRoles = rolesResponseSchema.safeParse(
        rolesResponse?.ok === true ? await rolesResponse.json().catch(() => null) : null,
      );
      if (alive.current && parsedRoles.success) {
        const byCode: Partial<Record<Level, string>> = {};
        for (const role of parsedRoles.data.data) {
          const level = LEVELS.find((entry) => entry === role.code);
          if (level !== undefined) byCode[level] = role.id;
        }
        setRoles(byCode);
      }
      const billing = await session.request('/billing').catch(() => null);
      const parsedBilling = billingDetailsSchema.safeParse(
        billing?.ok === true ? await billing.json().catch(() => null) : null,
      );
      if (alive.current && parsedBilling.success) {
        const { used, pending, limit } = parsedBilling.data.tenantSeats;
        setSeats({ used: used + pending, limit });
      }
    })();
    return () => {
      alive.current = false;
    };
  }, [workspace, session]);

  const limit = seats?.limit ?? null;
  const free = seats === null || limit === null ? null : Math.max(0, limit - seats.used);
  const reason =
    step === 1
      ? profileBlocked(profile, t)
      : step === 2
        ? workspaceBlocked(values, check, t)
        : step === 3
          ? invites.draft.trim() !== ''
            ? null
            : inviteBlocked(invites, free, t)
          : null;

  /* ── step 1 (flow A only): the name and the password chosen at last ─────────────────────────── */
  const saveProfile = async (): Promise<void> => {
    const response = await session
      .request('/setup/profile', {
        method: 'POST',
        body: JSON.stringify({
          familyName: profile.family.trim(),
          givenName: profile.given.trim(),
          password: profile.password,
        }),
      })
      .catch(() => null);
    if (response?.ok !== true) {
      const body = (await response?.json().catch(() => null)) as {
        error?: { code?: string };
      } | null;
      setRefusal(
        body?.error?.code === 'weak_password'
          ? t('setup.profile.blockedPassword')
          : t('error.unavailable'),
      );
      return;
    }
    /* The picture waits for step 2's workspace — `setup-store.ts`. */
    pendingAvatar.hold(profile.avatar);
    await session.reload();
  };

  /* ── step 2: the workspace, and the pictures that waited for it ─────────────────────────────── */
  const createWorkspace = async (): Promise<void> => {
    const response = await session
      .request('/setup/workspace', {
        method: 'POST',
        body: JSON.stringify({
          name: values.name.trim(),
          slug: values.slug,
          timezone: values.timezone,
          currency: values.currency,
        }),
      })
      .catch(() => null);
    if (response === null) {
      setRefusal(t('error.unavailable'));
      return;
    }
    if (!response.ok) {
      const body = (await response.json().catch(() => null)) as {
        error?: { code?: string };
      } | null;
      const code = body?.error?.code;
      setRefusal(
        code === 'slug_taken'
          ? t('setup.workspace.taken', { slug: values.slug })
          : code === 'slug_reserved'
            ? t('setup.workspace.reserved', { slug: values.slug })
            : code === 'already_member'
              ? t('setup.error.alreadyMember')
              : t('error.unavailable'),
      );
      return;
    }
    const parsed = setupWorkspaceResponseSchema.safeParse(await response.json().catch(() => null));
    if (!parsed.success) {
      setRefusal(t('error.unavailable'));
      return;
    }

    /* ⚠️ Never fatal: a picture that fails to upload leaves a letter, and the account and settings
       screens take one any time. The explicit header is the NEW workspace — `session.tsx` lets it
       win, as `workspace-dialog.tsx` relies on. */
    const upload = async (target: string, targetId: string, file: File): Promise<void> => {
      const form = new FormData();
      form.append('file', file);
      await session
        .request(`/files?target=${target}&targetId=${targetId}`, {
          method: 'POST',
          body: form,
          headers: { 'x-workspace-id': parsed.data.workspaceId },
        })
        .catch(() => null);
    };
    if (values.logo !== null) {
      await upload('workspace_icon', parsed.data.workspaceId, values.logo);
    }
    const avatar = pendingAvatar.take();
    if (avatar !== null) await upload('user_avatar', 'me', avatar);
    forgetAvatars();

    session.selectWorkspace(parsed.data.workspaceId);
    await session.reload();
  };

  /* ── back from step 3: step 2 again, on the saved workspace ────────────────────────────────── */
  const reopenWorkspace = (): void => {
    setBusy(true);
    setRefusal(null);
    void (async () => {
      const response = await session.request('/workspace').catch(() => null);
      const parsed = workspaceSettingsSchema.safeParse(
        response?.ok === true ? await response.json().catch(() => null) : null,
      );
      /* U-13 · the default currency, which step 2 made; the workspace settings do not carry it. */
      const money = await session.request('/workspace/currencies').catch(() => null);
      const currencies = currencySettingsSchema.safeParse(
        money?.ok === true ? await money.json().catch(() => null) : null,
      );
      const currency = currencies.success
        ? (currencies.data.currencies.find((one) => one.isDefault)?.code ?? null)
        : null;
      setBusy(false);
      if (!parsed.success) {
        setRefusal(t('error.unavailable'));
        return;
      }
      const zone = WORKSPACE_TIMEZONES.find((one) => one === parsed.data.defaultTimezone);
      setValues({
        name: parsed.data.name,
        slug: parsed.data.slug,
        /* ⚠️ Touched: the address is the saved one, and must not follow a renamed workspace. */
        slugTouched: true,
        logo: null,
        timezone: zone ?? values.timezone,
        currency: currency ?? values.currency,
      });
      setEditing({ version: parsed.data.version, currency });
    })();
  };

  /** Step 2 reopened: the same form, saved over the workspace rather than making another. */
  const updateWorkspace = async ({
    version,
    currency,
  }: {
    version: number;
    currency: string | null;
  }): Promise<void> => {
    /* ⚠️ U-13 · the currency first, through its own route — `PATCH /workspace` does not carry it,
       and a refusal here (setup finished, or money that is not the demo's) must leave the rest
       unsaved rather than half of it. */
    /* Unknown (the read failed): not sent, rather than guessed at. */
    if (currency !== null && values.currency !== currency) {
      const changed = await session
        .request('/setup/currency', {
          method: 'PUT',
          body: JSON.stringify({ currency: values.currency }),
        })
        .catch(() => null);
      if (changed?.ok !== true) {
        setRefusal(
          changed?.status === 409 ? t('setup.workspace.currencyLocked') : t('error.unavailable'),
        );
        return;
      }
      setEditing({ version, currency: values.currency });
    }
    const response = await session
      .request('/workspace', {
        method: 'PATCH',
        body: JSON.stringify({
          name: values.name.trim(),
          slug: values.slug,
          defaultTimezone: values.timezone,
          version,
        }),
      })
      .catch(() => null);
    if (response?.ok !== true) {
      const body = (await response?.json().catch(() => null)) as {
        error?: { code?: string };
      } | null;
      const code = body?.error?.code;
      setRefusal(
        code === 'slug_taken'
          ? t('setup.workspace.taken', { slug: values.slug })
          : code === 'slug_reserved'
            ? t('setup.workspace.reserved', { slug: values.slug })
            : t('error.unavailable'),
      );
      return;
    }
    if (values.logo !== null && workspace !== null) {
      const form = new FormData();
      form.append('file', values.logo);
      await session
        .request(`/files?target=workspace_icon&targetId=${workspace.id}`, {
          method: 'POST',
          body: form,
        })
        .catch(() => null);
      forgetAvatars();
    }
    /* The name and the address may have changed; the session carries both. */
    await session.reload();
    setEditing(null);
  };

  /* ── step 4: one invitation per address, and the ones that did not go ───────────────────────── */
  const sendInvites = async (): Promise<readonly string[]> => {
    const missed: string[] = [];
    let count = 0;
    for (const chip of invitable(invites)) {
      const roleId = roles[chip.level ?? invites.level];
      const response =
        roleId === undefined
          ? null
          : await session
              .request('/workspace/invitations', {
                method: 'POST',
                body: JSON.stringify({ email: chip.email, roleId }),
              })
              .catch(() => null);
      if (response?.ok === true) count += 1;
      else missed.push(chip.email);
    }
    setSent((before) => before + count);
    /* ⚠️ What went is out of the box, so a second *Belépés* cannot invite anybody twice; what did
       not go stays, for the person to fix or remove on step 3. */
    setInvites({
      ...invites,
      chips: invites.chips.filter(
        (chip) => chip.verdict.status !== 'ok' || missed.includes(chip.email),
      ),
    });
    setSeats((before) => (before === null ? before : { ...before, used: before.used + count }));
    return missed;
  };

  const submit = (): void => {
    if (busy) return;
    if (step === 3 && invites.draft.trim() !== '') return;
    if (reason !== null) return;
    setBusy(true);
    setRefusal(null);
    void (async () => {
      if (step === 1) await saveProfile();
      if (step === 2) {
        if (editing === null) await createWorkspace();
        else await updateWorkspace(editing);
      }
      if (step === 3) {
        /* Nothing is sent here — step 4's *Belépés* does it. */
        setSkipped(false);
        setFailures([]);
        setDone(true);
      }
      setBusy(false);
    })();
  };

  /* ── step 4: the flag, the white, the product ───────────────────────────────────────────────── */
  const enter = (): void => {
    setBusy(true);
    void (async () => {
      if (!skipped && invitable(invites).length > 0) {
        const missed = await sendInvites();
        if (missed.length > 0) {
          /* Back to step 3, naming what did not go — finishing now would leave it unsaid. */
          setFailures(missed);
          setDone(false);
          setBusy(false);
          return;
        }
      }
      await session.request('/setup/finish', { method: 'POST' }).catch(() => null);
      await welcome.play();
      await session.reload();
      setBusy(false);
    })();
  };

  return (
    <SetupFrame
      screen="setup"
      logo={{ src: logo, darkSrc: logoWhite, alt: t('app.name') }}
      step={step}
      total={4}
      stepLabel={t('setup.stepOf', { step, total: 4 })}
      onSubmit={submit}
      footnote={
        <span className="inline-flex items-center gap-1.5">
          {t('setup.signedIn', { email })}
          <span aria-hidden="true">·</span>
          <button
            type="button"
            className="hover:text-foreground underline-offset-2 hover:underline"
            onClick={() => {
              void session.signOut();
            }}
          >
            {t('setup.signOut')}
          </button>
        </span>
      }
      aside={
        <SetupPreview
          focus={step === 1 ? 'profile' : step === 2 ? 'workspace' : step === 3 ? 'people' : null}
          sharp={step === 4}
          name={name}
          initials={initials}
          email={email}
          avatarSrc={avatarSrc}
          workspaceName={workspaceName}
          logoSrc={logoSrc}
          invitees={step === 2 ? [] : invitable(invites).map((chip) => chip.email)}
        />
      }
    >
      <SetupStep key={step}>
        {step === 1 ? (
          <ProfileStep
            values={profile}
            onChange={setProfile}
            avatarSrc={pickedAvatar}
            avatarError={avatarError}
            onAvatarError={setAvatarError}
            email={email}
          />
        ) : step === 2 ? (
          <WorkspaceStep
            values={values}
            onChange={setValues}
            check={check}
            logoSrc={logoSrc}
            logoError={logoError}
            onLogoError={setLogoError}
            addressPrefix={`${host}/`}
          />
        ) : step === 3 ? (
          <InviteStep
            values={invites}
            onChange={setInvites}
            self={email}
            members={new Set([email.toLowerCase()])}
            seats={seats}
            failures={failures}
          />
        ) : (
          <DoneStep
            workspaceName={workspaceName}
            address={`${host}/${workspace?.slug ?? values.slug}`}
            logoSrc={logoSrc}
            avatarSrc={avatarSrc}
            workspaceInitial={(workspaceName.trim()[0] ?? 'W').toUpperCase()}
            personInitials={initials}
            sent={sent}
            pending={skipped ? 0 : invitable(invites).length}
            entering={busy}
            onEnter={enter}
            onBack={() => {
              setDone(false);
            }}
          />
        )}
      </SetupStep>
      {step === 4 ? null : (
        <SetupActions
          back={
            step === 3 || editing !== null ? (
              <Button
                type="button"
                variant="ghost"
                className="text-muted-foreground h-9 text-[13px] min-[900px]:-ml-3"
                disabled={busy}
                onClick={() => {
                  setRefusal(null);
                  if (step === 3) reopenWorkspace();
                  /* Back out of the reopened step 2 without saving: step 3 again, as it was. */ else
                    setEditing(null);
                }}
              >
                {t('setup.back')}
              </Button>
            ) : undefined
          }
          secondary={
            step === 3 ? (
              <Button
                type="button"
                variant="ghost"
                className="text-muted-foreground h-9 text-[13px]"
                disabled={busy}
                onClick={() => {
                  setFailures([]);
                  setSkipped(true);
                  setDone(true);
                }}
              >
                {t('setup.skip')}
              </Button>
            ) : undefined
          }
          primary={
            <Button
              type="submit"
              className="h-9 gap-2.5 px-4 text-[13px]"
              disabled={busy || reason !== null}
            >
              {busy
                ? t('setup.working')
                : step === 3
                  ? t('setup.invite.send')
                  : t('setup.continue')}
              <span aria-hidden="true" className="text-[12px] opacity-60">
                ↵
              </span>
            </Button>
          }
          reason={refusal ?? reason ?? ''}
        />
      )}
    </SetupFrame>
  );
}
